import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, PlayIcon, UsersIcon, AlertTriangleIcon, MicroscopeIcon, TrendingUpIcon, HandshakeIcon, BarChart3Icon, RocketIcon, ArrowDownIcon } from 'lucide-react';
interface StoryboardSlide {
  id: string;
  type: 'intro' | 'quiz' | 'reward' | 'summary' | 'cta';
  label: string;
  question?: string;
  objective?: string;
  color: string;
  icon: React.ElementType;
}
const storyboardData: StoryboardSlide[] = [{
  id: 'intro',
  type: 'intro',
  label: 'Introduction',
  question: 'How are oncology development priorities shifting?',
  objective: 'Hook the user with a clear value proposition: peer benchmarking, insights, and emerging priorities in 5 short questions.',
  color: '#4B9FE1',
  icon: PlayIcon
}, {
  id: 'q-role',
  type: 'quiz',
  label: 'Q1 — Role Segmentation',
  question: 'What best describes your role in oncology research or drug development?',
  objective: 'Segment the audience into 4 role groups (Strategic, Program, Technical, Partner) to enable personalized peer comparisons throughout the quiz.',
  color: '#4B9FE1',
  icon: UsersIcon
}, {
  id: 'q-uncertainty',
  type: 'quiz',
  label: 'Q2 — Problem Awareness',
  question: 'Where does the greatest uncertainty typically occur in oncology drug development?',
  objective: 'Surface awareness that biological uncertainty — not manufacturing or regulatory — is the primary failure point. Introduce the concept of understanding drug behaviour inside tumours.',
  color: '#EF4444',
  icon: AlertTriangleIcon
}, {
  id: 'q-confidence',
  type: 'quiz',
  label: 'Q3 — Mechanism Awareness',
  question: 'Which approach most improves early confidence in oncology drug development?',
  objective: 'Position molecular imaging as a distinct mechanism for gaining real-time biological insight, differentiating it from larger datasets or earlier trials.',
  color: '#34D399',
  icon: MicroscopeIcon
}, {
  id: 'q-evolution',
  type: 'quiz',
  label: 'Q4 — Industry Shift',
  question: 'How are imaging priorities evolving in oncology research today?',
  objective: 'Reframe imaging from a purely diagnostic tool to a central component of translational drug development — reflecting the broader industry shift.',
  color: '#F59E0B',
  icon: TrendingUpIcon
}, {
  id: 'q-partners',
  type: 'quiz',
  label: 'Q5 — Partner Selection',
  question: 'When selecting partners for oncology drug development, what matters most today?',
  objective: 'Shift thinking from isolated best-in-class tools toward integrated translational platforms that reduce coordination complexity.',
  color: '#A78BFA',
  icon: HandshakeIcon
}, {
  id: 'summary',
  type: 'summary',
  label: 'Results — Role Comparison',
  question: 'How roles compare across all questions',
  objective: 'Show per-question breakdowns with role-specific bars so users can see where their priorities diverge from other roles — driving curiosity and engagement.',
  color: '#4B9FE1',
  icon: BarChart3Icon
}, {
  id: 'cta',
  type: 'cta',
  label: 'Call to Action',
  question: 'See how oncology research priorities are evolving.',
  objective: 'Convert engagement into action: direct users to Minerva publications and about page via embedded iframes within the experience.',
  color: '#4B9FE1',
  icon: RocketIcon
}];
export function StoryboardView({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  return <AnimatePresence data-id="element-186">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-[100] bg-[#070D15] flex flex-col" data-id="element-187">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 flex-shrink-0" data-id="element-188">
            <div data-id="element-189">
              <h2 className="text-white font-semibold text-lg" data-id="element-190">Storyboard</h2>
              <p className="text-slate-500 text-xs mt-0.5" data-id="element-191">
                Quiz flow & objectives
              </p>
            </div>
            <button onClick={onClose} className="p-2 text-slate-500 hover:text-white hover:bg-white/10 rounded-lg transition-colors" aria-label="Close storyboard" data-id="element-192">
              <XIcon className="w-5 h-5" data-id="element-193" />
            </button>
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto px-5 py-6 no-scrollbar" data-id="element-194">
            <div className="max-w-lg mx-auto space-y-2" data-id="element-195">
              {storyboardData.map((slide, index) => {
            const Icon = slide.icon;
            const isLast = index === storyboardData.length - 1;
            return <motion.div key={slide.id} initial={{
              opacity: 0,
              y: 12
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: index * 0.06
            }} data-id="element-196">
                    {/* Card */}
                    <div className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-4 relative" data-id="element-197">
                      {/* Type badge */}
                      <div className="flex items-center justify-between mb-3" data-id="element-198">
                        <div className="flex items-center space-x-2.5" data-id="element-199">
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{
                      backgroundColor: `${slide.color}15`,
                      border: `1px solid ${slide.color}30`
                    }} data-id="element-200">
                            <Icon className="w-4 h-4" style={{
                        color: slide.color
                      }} data-id="element-201" />
                          </div>
                          <span className="text-xs font-bold uppercase tracking-widest" style={{
                      color: slide.color
                    }} data-id="element-202">
                            {slide.label}
                          </span>
                        </div>
                        <span className="text-[10px] text-slate-600 uppercase tracking-wider font-medium bg-white/5 px-2 py-0.5 rounded" data-id="element-203">
                          {slide.type}
                        </span>
                      </div>

                      {/* Question */}
                      {slide.question && <p className="text-sm text-slate-300 leading-relaxed mb-3" data-id="element-204">
                          {slide.question}
                        </p>}

                      {/* Objective */}
                      {slide.objective && <div className="bg-white/[0.03] border border-white/[0.06] rounded-lg px-3 py-2.5" data-id="element-205">
                          <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5" data-id="element-206">
                            Objective
                          </p>
                          <p className="text-xs text-slate-400 leading-relaxed" data-id="element-207">
                            {slide.objective}
                          </p>
                        </div>}
                    </div>

                    {/* Flow arrow */}
                    {!isLast && <div className="flex justify-center py-1" data-id="element-208">
                        <ArrowDownIcon className="w-3.5 h-3.5 text-slate-700" data-id="element-209" />
                      </div>}
                  </motion.div>;
          })}
            </div>

            {/* Footer note */}
            <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.6
        }} className="text-center text-[10px] text-slate-600 mt-8 mb-4 uppercase tracking-widest" data-id="element-210">
              Each question includes a reward slide with peer poll data,
              role-specific breakdowns, and visualizations.
            </motion.p>
          </div>
        </motion.div>}
    </AnimatePresence>;
}